import React from 'react';
import { cn } from '../utils';

export interface ProgressProps {
  value: number;
  max?: number;
  variant?: 'primary' | 'secondary' | 'accent' | 'info' | 'success' | 'warning' | 'error';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  className?: string;
}

export const Progress: React.FC<ProgressProps> = ({
  value,
  max = 100,
  variant = 'primary',
  size = 'md',
  showLabel = false,
  className,
}) => {
  const percentage = Math.round((value / max) * 100);

  const progressClasses = cn(
    'progress',
    variant === 'primary' && 'progress-primary',
    variant === 'secondary' && 'progress-secondary',
    variant === 'accent' && 'progress-accent',
    variant === 'info' && 'progress-info',
    variant === 'success' && 'progress-success',
    variant === 'warning' && 'progress-warning',
    variant === 'error' && 'progress-error',
    size === 'xs' && 'progress-xs',
    size === 'sm' && 'progress-sm',
    size === 'lg' && 'progress-lg',
    className
  );

  return (
    <div className="w-full">
      <progress
        className={progressClasses}
        value={value}
        max={max}
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
      />
      {showLabel && <div className="text-sm text-base-content/70 mt-1">{percentage}%</div>}
    </div>
  );
};

export interface RadialProgressProps {
  value: number;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  thickness?: number;
  showLabel?: boolean;
  className?: string;
}

export const RadialProgress: React.FC<RadialProgressProps> = ({
  value,
  size = 'md',
  thickness = 4,
  showLabel = true,
  className,
}) => {
  const sizeClasses = {
    sm: 'w-12 h-12',
    md: 'w-16 h-16',
    lg: 'w-20 h-20',
    xl: 'w-24 h-24',
  };

  return (
    <div
      className={cn('radial-progress', sizeClasses[size], className)}
      style={{ '--value': value, '--thickness': `${thickness}px` } as React.CSSProperties}
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      {showLabel && `${value}%`}
    </div>
  );
};
